import { useTaskContext } from '@/context/house/tasks/TaskContext.tsx';
import { taskSelectors } from '@/utils/taskUtils.ts';
import { makeLogger } from '@/utils/logger/logger.ts';

const logger = makeLogger('useHouseTasks');
import { ApiTask } from '@/types/tasks.ts';
import { useHouseApiTasks } from '../tasks/useHouseApiTasks.ts';
import { useEffect, useMemo, useRef } from 'react';
import {
	isApiTaskFailed,
	isApiTaskInProgress,
	isApiTaskPending,
	mapApiStatusToTaskStatus,
} from '@/utils/taskStatusUtils';

/**
 * Hook to filter tasks related to a specific house
 * Now also loads tasks from server on initialization
 * @param houseId - The ID of the house to filter tasks for
 * @returns Various filtered task collections
 */
export const useHouseTasks = (houseId: number | null) => {
	const { tasks, addTask } = useTaskContext();

	// Track which tasks have been restored to prevent duplicates
	const restoredTaskIdsRef = useRef<Set<string>>(new Set());

	// Always fetch once on initial load to restore missing tasks, but don't poll continuously
	// The polling will be handled by useTaskSync when there are active tasks
	const apiTaskOptions = useMemo(() => {
		return { immediate: true, shouldPoll: false };
	}, []);
	const { data: apiTasks = [] } = useHouseApiTasks(houseId, apiTaskOptions) as {
		data: ApiTask[];
	};

	// Clear restored tasks tracking when house changes
	useEffect(() => {
		restoredTaskIdsRef.current.clear();
	}, [houseId]);

	// Restore missing active tasks from server (pending, in-progress, and failed)
	useEffect(() => {
		if (!houseId || !apiTasks.length) return;

		const localTaskIds = new Set(tasks.map((t) => t.id));
		const missingTasks = apiTasks.filter((apiTask: ApiTask) => {
			return (
				!localTaskIds.has(apiTask.task_id) &&
				!restoredTaskIdsRef.current.has(apiTask.task_id) &&
				(isApiTaskPending(apiTask.status) ||
					isApiTaskInProgress(apiTask.status) ||
					// TODO: Bun-126 Review if this is correct
					// I also want to restore failed tasks in case they were lost due to app crash or clearing local storage
					// This is done for finalizing transfer tasks to allow retrying failed transfers
					// If this leads to problem, we can filter by type as well
					isApiTaskFailed(apiTask.status))
			);
		});

		// Add missing active tasks (pending/in-progress/failed) back to local state
		missingTasks.forEach((apiTask: ApiTask) => {
			const restoredTask = {
				id: apiTask.task_id,
				type: apiTask.metadata.task_type,
				status: mapApiStatusToTaskStatus(apiTask.status),
				progress: apiTask.progress,
				metadata: {
					houseId,
					// Try to extract other metadata from API response
					...apiTask.metadata,
				},
				isPollingTask: true,
				createdAt: new Date(parseInt(apiTask.metadata.created_at) * 1000).toISOString(),
			};

			logger.debug(
				`Restoring missing task from server: ${apiTask.task_id} (${apiTask.metadata.task_type}) - status: ${apiTask.status}`,
			);

			// Mark as restored BEFORE adding to prevent duplicate detection
			restoredTaskIdsRef.current.add(apiTask.task_id);
			addTask(restoredTask);
		});
	}, [houseId, apiTasks, tasks, addTask]);

	// Create unified tasks that merge local tasks with API statuses
	const houseTasks = useMemo(() => {
		if (!houseId) return [];

		const localHouseTasks = taskSelectors.byHouse(tasks, houseId);

		// If no API tasks available, return local tasks as-is
		if (!apiTasks.length) return localHouseTasks;

		// Create a map of API tasks by task_id for quick lookup
		const apiTaskMap = new Map<string, ApiTask>();
		apiTasks.forEach((apiTask) => {
			apiTaskMap.set(apiTask.task_id, apiTask);
		});

		// Merge local tasks with API statuses
		return localHouseTasks.map((localTask) => {
			const apiTask = apiTaskMap.get(localTask.id);

			// If we have API data for this task, use the API status and progress
			if (apiTask) {
				return {
					...localTask,
					status: mapApiStatusToTaskStatus(apiTask.status),
					progress: apiTask.progress,
				};
			}

			// Otherwise, return the local task as-is
			return localTask;
		});
	}, [houseId, tasks, apiTasks]);
	const maintenanceScheduleTasks = taskSelectors.filterIsGeneratingMaintenanceTask(houseTasks);

	const isGeneratingMaintenanceSchedule = maintenanceScheduleTasks.length > 0;
	const maintenanceScheduleTask = isGeneratingMaintenanceSchedule ? maintenanceScheduleTasks[0] : null;

	return {
		houseTasks,
		isGeneratingMaintenanceSchedule,
		maintenanceScheduleTask,
	};
};

export default useHouseTasks;
